import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class HtmlSanitizerService {
  private readonly allowedStyleProperties = [
    'width',
    'height',
    'background-color',
    'color',
    'border-width',
    'border-color',
    'border-style',
    'text-align',
    'font-weight',
    'font-size',
    'margin',
    'padding',
    'display',
    'float',
  ];

  private readonly allowedTags = [
    'div',
    'span',
    'p',
    'br',
    'strong',
    'em',
    'u',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'table',
    'tbody',
    'thead',
    'tr',
    'td',
    'th',
    'ul',
    'ol',
    'li',
    'a',
    'img',
    'sup',
    'vimeo',
  ];

  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Sanitizes HTML content while preserving safe styling
   * @param html - The HTML content to sanitize
   * @param allowTrustedContent - If true, bypasses sanitization (use only for trusted content)
   * @returns SafeHtml that can be used with [innerHTML]
   */
  sanitizeHtml(html: string, allowTrustedContent = false): SafeHtml {
    if (!html) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    if (allowTrustedContent) {
      return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    const sanitizedHtml = this.sanitizeContent(html);
    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  private sanitizeContent(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    this.cleanElement(tempDiv);

    return tempDiv.innerHTML;
  }

  private cleanElement(element: Element): void {
    const children = Array.from(element.children);

    for (const child of children) {
      if (!this.allowedTags.includes(child.tagName.toLowerCase())) {
        const span = document.createElement('span');
        span.innerHTML = child.innerHTML;
        child.parentNode?.replaceChild(span, child);
        this.cleanElement(span);
        continue;
      }

      this.cleanAttributes(child as HTMLElement);

      this.cleanElement(child);
    }
  }

  private cleanAttributes(element: HTMLElement): void {
    const attributesToRemove: string[] = [];

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];

      if (attr.name === 'style') {
        element.style.cssText = this.sanitizeStyles(element.style.cssText);
      } else if (attr.name === 'class') {
        continue;
      } else if (
        attr.name.startsWith('data-') &&
        (this.isTableAttribute(attr.name) ||
          this.isReferenceAttribute(attr.name))
      ) {
        continue;
      } else if (
        this.isPrimeVideoAttribute(element, attr.name) ||
        this.isLegacyVimeoIdAttribute(element, attr.name)
      ) {
        continue;
      } else if (
        [
          'href',
          'src',
          'alt',
          'title',
          'colspan',
          'rowspan',
          'width',
          'height',
          'type',
          'contenteditable',
        ].includes(attr.name)
      ) {
        if (attr.name === 'href' || attr.name === 'src') {
          if (!this.isValidUrl(attr.value)) {
            attributesToRemove.push(attr.name);
          }
        }
      } else {
        attributesToRemove.push(attr.name);
      }
    }

    attributesToRemove.forEach((attrName) => {
      element.removeAttribute(attrName);
    });
  }

  private sanitizeStyles(cssText: string): string {
    const allowedStyles: string[] = [];

    const declarations = cssText.split(';').filter((decl) => decl.trim());

    for (const declaration of declarations) {
      const [property, value] = declaration.split(':').map((s) => s.trim());

      if (property && value && this.allowedStyleProperties.includes(property)) {
        if (this.isValidCssValue(property, value)) {
          allowedStyles.push(`${property}: ${value}`);
        }
      }
    }

    return allowedStyles.join('; ');
  }

  private isTableAttribute(attrName: string): boolean {
    return ['data-row', 'data-cell', 'data-class'].includes(attrName);
  }

  private isReferenceAttribute(attrName: string): boolean {
    return ['data-reference', 'data-reference-uid', 'data-unique-id'].includes(
      attrName
    );
  }

  private isPrimeVideoAttribute(
    element: HTMLElement,
    attrName: string
  ): boolean {
    if (element.tagName.toLowerCase() !== 'div') {
      return false;
    }

    return attrName === 'primevideo' || attrName === 'vimeoid';
  }

  private isLegacyVimeoIdAttribute(
    element: HTMLElement,
    attrName: string
  ): boolean {
    return element.tagName.toLowerCase() === 'vimeo' && attrName === 'id';
  }

  private isValidCssValue(property: string, value: string): boolean {
    const colorRegex =
      /^(rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|#[0-9a-fA-F]{3,6}|[a-zA-Z]+)$/;
    const sizeRegex = /^\d+(\.\d+)?(px|em|rem|%|vh|vw)$/;
    const numberRegex = /^\d+(\.\d+)?$/;

    switch (property) {
      case 'background-color':
      case 'color':
      case 'border-color':
        return colorRegex.test(value);
      case 'width':
      case 'height':
      case 'border-width':
      case 'margin':
      case 'padding':
      case 'font-size':
        return sizeRegex.test(value) || numberRegex.test(value);
      case 'font-weight':
        return [
          'normal',
          'bold',
          'lighter',
          'bolder',
          '100',
          '200',
          '300',
          '400',
          '500',
          '600',
          '700',
          '800',
          '900',
        ].includes(value);
      case 'text-align':
        return ['left', 'right', 'center', 'justify'].includes(value);
      case 'border-style':
        return ['solid', 'dashed', 'dotted', 'none'].includes(value);
      case 'display':
        return [
          'block',
          'inline',
          'inline-block',
          'table',
          'table-cell',
          'table-row',
          'none',
        ].includes(value);
      case 'float':
        return ['left', 'right', 'none'].includes(value);
      default:
        return false;
    }
  }

  private isValidUrl(url: string): boolean {
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return true; // Relative URLs
    }

    try {
      const urlObj = new URL(url);
      return ['http:', 'https:', 'mailto:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }
}
